import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { QuestionPalette } from './ui/QuestionPalette';
import { ArrowLeft, Menu, BookOpen, Clock, Volume2, Loader2, Play, Pause, RotateCcw, SkipForward } from 'lucide-react';
import login_hero_image from '../assets/eko-_transparent-removebg-preview.png';
import TestBadge from './TestBadge';
import { 
  getComprehensionStories, 
  submitComprehensionTest,
  ComprehensionStoriesResponse,
  ComprehensionStory,
  StoryAnswers 
} from '../services/api';

// =============================================
// TYPES & INTERFACES
// =============================================
interface Question {
  id: string;
  text: string;
  options: string[];
  storyId: string;
  storyIndex: number;
}

interface Passage {
  id: string;
  title: string;
  directions: string;
  content: string;
  audioBase64: string;
  questionRange: [number, number];
}

// =============================================
// PROPS INTERFACE
// =============================================
interface ReadingTestProps {
  onComplete?: (results: any) => void;
  onBackToDashboard?: () => void;
  playerName?: string;
  gradeName?: string;
  childId?: string;
}

// =============================================
// MAIN COMPONENT
// =============================================
const ReadingTest: React.FC<ReadingTestProps> = ({
  onComplete,
  onBackToDashboard,
  playerName = 'Student',
  gradeName = 'Grade 1',
  childId = '',
}) => {
  const navigate = useNavigate();

  // Loading & Error states
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Loading stories...');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Data from API
  const [storiesData, setStoriesData] = useState<ComprehensionStoriesResponse | null>(null);
  const [passages, setPassages] = useState<Passage[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);

  // Test state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<number, number>>(new Map()); // Map<questionIndex, selectedOptionIndex>
  const [attemptedIndices, setAttemptedIndices] = useState<number[]>([]);
  const [skippedIndices, setSkippedIndices] = useState<number[]>([]);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [currentQuestionTime, setCurrentQuestionTime] = useState<number>(0);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Audio state
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentAudioStoryId, setCurrentAudioStoryId] = useState<string | null>(null);

  // Story transition state - shows a separator screen when moving to a new story
  const [showStoryTransition, setShowStoryTransition] = useState(false);
  const [pendingStoryIndex, setPendingStoryIndex] = useState<number | null>(null);
  const prevStoryIndexRef = useRef<number>(0);

  // Fetch stories on mount
  useEffect(() => {
    const fetchStories = async () => {
      if (!childId) {
        setError('Child ID is required to load the reading test.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setLoadingMessage('Fetching stories and questions...');
        
        const data = await getComprehensionStories(childId, gradeName);
        setStoriesData(data);

        // Transform API data to component format
        const transformedPassages: Passage[] = [];
        const transformedQuestions: Question[] = [];
        let questionIndex = 0;

        data.stories.forEach((story: ComprehensionStory, storyIndex: number) => {
          const startIndex = questionIndex;
          
          story.questions.forEach((q) => {
            transformedQuestions.push({
              id: q.id,
              text: q.question,
              options: q.options,
              storyId: story.story_id,
              storyIndex: storyIndex,
            });
            questionIndex++;
          });

          transformedPassages.push({
            id: story.story_id,
            title: story.title,
            directions: data.instructions,
            content: story.story_text,
            audioBase64: story.story_audio_base64,
            questionRange: [startIndex, questionIndex - 1],
          });
        });

        setPassages(transformedPassages);
        setQuestions(transformedQuestions);
        setIsLoading(false);
      } catch (err: any) {
        console.error('Error fetching comprehension stories:', err);
        setError(err.message || 'Failed to load stories. Please try again.');
        setIsLoading(false);
      }
    };

    fetchStories();
  }, [childId, gradeName]);

  // Audio handlers
  const playAudio = (audioBase64: string, storyId: string) => {
    // Stop current audio if playing different story
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    try {
      // Decode base64 to blob
      const byteCharacters = atob(audioBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const audioBlob = new Blob([byteArray], { type: 'audio/mp3' });
      const audioUrl = URL.createObjectURL(audioBlob);

      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      setCurrentAudioStoryId(storyId);

      audio.addEventListener('loadedmetadata', () => {
        setAudioDuration(audio.duration);
      });

      audio.addEventListener('timeupdate', () => {
        setAudioProgress(audio.currentTime);
      });

      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setAudioProgress(0);
      });

      audio.play();
      setIsPlaying(true);
    } catch (err) {
      console.error('Error playing audio:', err);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIndex];
  const currentPassage = currentQuestion 
    ? passages.find((p) => p.id === currentQuestion.storyId)
    : null;

  // Story tracking
  const totalStories = passages.length;
  const currentStoryIndex = currentQuestion ? currentQuestion.storyIndex : 0;
  const currentStoryNumber = currentStoryIndex + 1;
  
  // Get question range for current story
  const currentStoryQuestionStart = currentPassage ? currentPassage.questionRange[0] : 0;
  const currentStoryQuestionEnd = currentPassage ? currentPassage.questionRange[1] : 0;
  const questionsInCurrentStory = currentStoryQuestionEnd - currentStoryQuestionStart + 1;
  const currentQuestionInStory = currentIndex - currentStoryQuestionStart + 1;

  // Function to skip to next story
  const handleSkipToNextStory = () => {
    if (currentStoryIndex >= totalStories - 1) {
      // Already on last story, submit test
      handleSubmit();
      return;
    }
    
    // Mark all questions in current story as skipped
    const newSkipped = [...skippedIndices];
    for (let i = currentStoryQuestionStart; i <= currentStoryQuestionEnd; i++) {
      if (!attemptedIndices.includes(i) && !newSkipped.includes(i)) {
        newSkipped.push(i);
      }
    }
    setSkippedIndices(newSkipped);
    
    // Find the first question of the next story
    const nextStory = passages[currentStoryIndex + 1];
    if (nextStory) {
      setCurrentIndex(nextStory.questionRange[0]);
      // Stop audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  // Function to jump to a specific story
  const handleJumpToStory = (storyIndex: number) => {
    if (storyIndex < 0 || storyIndex >= totalStories) return;
    const targetStory = passages[storyIndex];
    if (targetStory) {
      setCurrentIndex(targetStory.questionRange[0]);
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const progressPercentage = useMemo(
    () => totalQuestions > 0 ? ((currentIndex + 1) / totalQuestions) * 100 : 0,
    [currentIndex, totalQuestions]
  );

  // Motivational messages
  const motivationalMessages = useMemo(() => [
    `Great start, {name}!`,
    `Awesome work, {name}!`,
    `Keep going, {name}!`,
    `You're doing great, {name}!`
  ], []);

  const firstName = useMemo(() => (playerName?.split(" ")[0] || playerName || "Student"), [playerName]);

  const currentMotivation = useMemo(() => {
    if (!totalQuestions || totalQuestions <= 0) return motivationalMessages[0].replace('{name}', firstName);
    const segments = motivationalMessages.length;
    const segmentSize = Math.max(1, Math.floor(totalQuestions / segments));
    const idx = Math.min(segments - 1, Math.floor(currentIndex / segmentSize));
    return motivationalMessages[idx].replace('{name}', firstName);
  }, [currentIndex, totalQuestions, motivationalMessages, firstName]);

  // Mobile / Tablet detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
  const [showPassage, setShowPassage] = useState(true);
  
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Timer for current question
  useEffect(() => {
    setStartTime(Date.now());
    setCurrentQuestionTime(0);
  }, [currentIndex]);

  // Detect story change and show transition screen so the user knows a new passage has started
  useEffect(() => {
    if (prevStoryIndexRef.current !== currentStoryIndex) {
      // Only show the transition when moving forward to a NEW story (not on very first load)
      if (prevStoryIndexRef.current !== null && currentStoryIndex > prevStoryIndexRef.current) {
        setPendingStoryIndex(currentStoryIndex);
        setShowStoryTransition(true);
        // Stop any playing audio when the transition appears
        if (audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      }
      prevStoryIndexRef.current = currentStoryIndex;
    }
  }, [currentStoryIndex]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentQuestionTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime]);

  // Audio toggle handler
  const toggleAudio = () => {
    if (!currentPassage) return;

    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else if (audioRef.current && currentAudioStoryId === currentPassage.id) {
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      playAudio(currentPassage.audioBase64, currentPassage.id);
    }
  };

  const restartAudio = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setIsPlaying(true);
    } else if (currentPassage) {
      playAudio(currentPassage.audioBase64, currentPassage.id);
    }
  };

  // Handlers
  const handleSelectOption = (optionIndex: number) => {
    setAnswers(prev => {
      const newAnswers = new Map(prev);
      newAnswers.set(currentIndex, optionIndex);
      return newAnswers;
    });
  };

  const findNextUnattemptedOrSkippedQuestion = (): number => {
    for (let i = currentIndex + 1; i < totalQuestions; i++) {
      if (skippedIndices.includes(i) && !attemptedIndices.includes(i)) return i;
    }
    for (let i = currentIndex + 1; i < totalQuestions; i++) {
      if (!attemptedIndices.includes(i) && !skippedIndices.includes(i)) return i;
    }
    for (let i = 0; i < currentIndex; i++) {
      if (skippedIndices.includes(i) && !attemptedIndices.includes(i)) return i;
    }
    for (let i = 0; i < currentIndex; i++) {
      if (!attemptedIndices.includes(i) && !skippedIndices.includes(i)) return i;
    }
    return -1;
  };

  const handleNext = () => {
    const hasAnswer = answers.has(currentIndex);
    
    if (!hasAnswer) {
      alert('Please select an answer before proceeding.');
      return;
    }

    // Mark as attempted
    setAttemptedIndices((prev) =>
      prev.includes(currentIndex) ? prev : [...prev, currentIndex]
    );
    setSkippedIndices((prev) => prev.filter(index => index !== currentIndex));

    // Check if last question
    if (currentIndex === totalQuestions - 1) {
      handleSubmit();
      return;
    }

    // Move to next unattempted question
    const nextIndex = findNextUnattemptedOrSkippedQuestion();
    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
    } else {
      handleSubmit();
    }
  };

  const handleSkip = () => {
    if (!skippedIndices.includes(currentIndex)) {
      setSkippedIndices((prev) => [...prev, currentIndex]);
    }

    if (currentIndex === totalQuestions - 1) {
      const nextIndex = findNextUnattemptedOrSkippedQuestion();
      if (nextIndex !== -1) {
        setCurrentIndex(nextIndex);
      } else {
        handleSubmit();
      }
      return;
    }

    const nextIndex = findNextUnattemptedOrSkippedQuestion();
    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!storiesData || !childId) {
      console.error('Missing required data for submission');
      return;
    }

    // Stop audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }

    try {
      setIsSubmitting(true);

      // Build story_answers array in API format
      const storyAnswersMap = new Map<string, { question_id: string; selected_index: number }[]>();

      questions.forEach((q, idx) => {
        const selectedIndex = answers.get(idx);
        if (selectedIndex !== undefined) {
          if (!storyAnswersMap.has(q.storyId)) {
            storyAnswersMap.set(q.storyId, []);
          }
          storyAnswersMap.get(q.storyId)!.push({
            question_id: q.id,
            selected_index: selectedIndex,
          });
        }
      });

      const storyAnswers: StoryAnswers[] = [];
      storyAnswersMap.forEach((answers, storyId) => {
        storyAnswers.push({
          story_id: storyId,
          answers: answers,
        });
      });

      const result = await submitComprehensionTest(childId, gradeName, storyAnswers);

      if (onComplete) {
        onComplete(result);
      } else {
        console.log('Test completed:', result);
        navigate(-1);
      }
    } catch (err: any) {
      console.error('Error submitting test:', err);
      setIsSubmitting(false);
      alert('Failed to submit test. Please try again.');
    }
  };

  const handleBack = () => {
    // Stop audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Use the callback to go back to learning journey
    if (onBackToDashboard) {
      onBackToDashboard();
    } else {
      navigate(-1);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const selectedOption = answers.get(currentIndex);

  // Loading state
  if (isLoading) {
    return (
      <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] items-center justify-center">
        <div className="text-center">
          <TestBadge test="reading" size="lg" className="mb-4" />
          <Loader2 className="w-12 h-12 animate-spin text-[#8E7BFF] mx-auto mb-4" />
          <p className="text-lg text-[#6B6FA3]">{loadingMessage}</p>
          <p className="text-sm text-[#6B6FA3] mt-2">This may take a few moments...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-[#1F224B] mb-2">Oops!</h2>
          <p className="text-[#6B6FA3] mb-4">{error}</p>
          <Button 
            onClick={handleBack}
            className="bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white"
          >
            Go Back
          </Button>
        </Card>
      </div>
    );
  }

  // No data state
  if (!currentQuestion || !currentPassage) {
    return (
      <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <div className="text-yellow-500 text-5xl mb-4">📚</div>
          <h2 className="text-xl font-bold text-[#1F224B] mb-2">No Stories Available</h2>
          <p className="text-[#6B6FA3] mb-4">There are no reading comprehension stories available for this grade level.</p>
          <Button 
            onClick={handleBack}
            className="bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white"
          >
            Go Back
          </Button>
        </Card>
      </div>
    );
  }

  // Submitting state overlay
  if (isSubmitting) {
    return (
      <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#8E7BFF] mx-auto mb-4" />
          <p className="text-lg text-[#6B6FA3]">Submitting your answers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] flex-col">
      {/* Story Transition Overlay */}
      {showStoryTransition && pendingStoryIndex !== null && passages[pendingStoryIndex] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 text-center animate-[fadeIn_0.3s_ease-out]">
            <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] flex items-center justify-center mb-5 shadow-lg">
              <BookOpen className="w-10 h-10 text-white" />
            </div>

            <div className="inline-block px-4 py-1 rounded-full bg-[#F3E8FF] text-[#8E7BFF] text-sm font-bold mb-4">
              Story {pendingStoryIndex + 1} of {totalStories}
            </div>

            <h2 className="text-2xl font-bold text-[#1F224B] mb-2">
              New Story Time!
            </h2>
            <p className="text-[#6B6FA3] mb-1">You finished the previous story. Now let's read the next one:</p>
            <p className="text-lg font-semibold text-[#1F224B] mb-6 italic">
              "{passages[pendingStoryIndex].title}"
            </p>

            <div className="bg-gradient-to-br from-[#FFF7EE] to-[#F3E8FF] rounded-2xl p-4 mb-6 border-2 border-[#EDE9FF]">
              <p className="text-sm text-[#6B6FA3] leading-relaxed">
                📖 Please read the new story carefully (or listen to the audio) before answering the questions.
              </p>
            </div>

            <Button
              onClick={() => {
                setShowStoryTransition(false);
                setPendingStoryIndex(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="w-full h-12 rounded-full text-lg font-bold text-white bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-95 shadow-lg"
            >
              Start Reading →
            </Button>
          </div>
        </div>
      )}

      <div className="three-col-layout flex flex-1 gap-4 lg:gap-6 items-start unified-medium-scale">
        {/* Desktop: Left Question Navigation */}
        <div className="left-col hidden lg:flex lg:flex-col lg:w-96 xl:w-[420px] p-4 flex-shrink-0 unified-medium-panel">
          <div className="sticky top-20">
            {/* Story Navigation Card */}
            <div className="w-full p-4 rounded-2xl bg-gradient-to-b from-[#FFF7EE] to-white/80 shadow-md border border-[#F7B26A]/30 mb-4">
              <h3 className="font-bold text-[#1F224B] mb-3 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#F7B26A]" />
                Stories
              </h3>
              <div className="space-y-2">
                {passages.map((passage, idx) => {
                  const isCurrentStory = idx === currentStoryIndex;
                  const storyQuestions = questions.filter(q => q.storyIndex === idx);
                  const answeredInStory = storyQuestions.filter((_, qIdx) => 
                    attemptedIndices.includes(passage.questionRange[0] + qIdx)
                  ).length;
                  
                  return (
                    <button
                      key={passage.id}
                      onClick={() => handleJumpToStory(idx)}
                      className={`w-full text-left p-3 rounded-xl transition-all ${
                        isCurrentStory
                          ? 'bg-gradient-to-r from-[#F7B26A]/20 to-[#ECA7C1]/20 border-2 border-[#F7B26A]'
                          : 'bg-white/80 border border-[#EDE9FF] hover:border-[#ECA7C1]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-semibold ${isCurrentStory ? 'text-[#F7B26A]' : 'text-[#1F224B]'}`}>
                          Story {idx + 1}
                        </span>
                        <span className="text-xs text-[#6B6FA3]">
                          {answeredInStory}/{storyQuestions.length} answered
                        </span>
                      </div>
                      <p className="text-xs text-[#6B6FA3] mt-1 truncate">{passage.title}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Question Palette */}
            <div className="w-full p-4 rounded-2xl bg-gradient-to-b from-[#F3E8FF] to-white/80 shadow-md border border-gray-100 lg:origin-top-left unified-medium-inner">
              <QuestionPalette
                total={totalQuestions}
                currentIndex={currentIndex}
                attempted={attemptedIndices}
                skipped={skippedIndices}
                onJump={(idx) => {
                  if (skippedIndices.includes(idx) || idx === currentIndex) {
                    setCurrentIndex(idx);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                isMobile={false}
                isTablet={false}
                isDesktopLeft={true}
                collapsed={false}
              />
            </div>
          </div>
        </div>

        {/* Center Main Card */}
        <div className="center-col flex-1 flex flex-col items-center lg:origin-top-left min-w-0 unified-medium-panel">
          <div className="center-inner w-full max-w-3xl mx-auto mt-0 md:mt-4 p-2 md:p-4 unified-medium-inner flex flex-col flex-1">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <Button
                onClick={handleBack}
                variant="outline"
                size="sm"
                className="bg-white/80 hover:bg-white"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>

              <div className="flex items-center gap-3">
                <div className="text-right">
                  <TestBadge test="reading" subject={gradeName} />
                  {isMobile && <div className="text-xs text-[#6B6FA3]">{currentMotivation}</div>}
                </div>

                {/* Mobile Menu Button */}
                <div className="relative lg:hidden">
                  <Button
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    variant="outline"
                    size="sm"
                    className="bg-white/80 hover:bg-white p-2"
                  >
                    <Menu className="w-4 h-4" />
                  </Button>

                  {showMobileMenu && (
                    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 p-4">
                      <div className="mb-4">
                        <h3 className="font-bold text-[#1F224B] mb-2">Questions</h3>
                        <QuestionPalette
                          total={totalQuestions}
                          currentIndex={currentIndex}
                          attempted={attemptedIndices}
                          skipped={skippedIndices}
                          onJump={(idx) => {
                            if (skippedIndices.includes(idx) || idx === currentIndex) {
                              setCurrentIndex(idx);
                              setShowMobileMenu(false);
                            }
                          }}
                          isMobile={true}
                          isTablet={false}
                          isDesktopLeft={false}
                          collapsed={false}
                        />
                      </div>
                      <div className="border-t pt-4">
                        <Button
                          onClick={() => {
                            handleSubmit();
                            setShowMobileMenu(false);
                          }}
                          className="w-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white"
                        >
                          End Test
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Main Card */}
            <Card className="p-4 sm:p-6 rounded-3xl shadow-xl bg-white/95 flex flex-col h-full overflow-hidden">
              {/* Story Indicator & Navigation */}
              <div className="mb-4 flex items-center justify-between bg-gradient-to-r from-[#8E7BFF]/10 to-[#ECA7C1]/10 rounded-xl p-3 border border-[#EDE9FF]">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-[#8E7BFF]" />
                    <span className="font-bold text-[#1F224B]">
                      Story {currentStoryNumber} of {totalStories}
                    </span>
                  </div>
                  <span className="text-xs text-[#6B6FA3] hidden sm:inline">
                    (Question {currentQuestionInStory} of {questionsInCurrentStory})
                  </span>
                </div>
                
                {/* Story Navigation Pills */}
                <div className="flex items-center gap-2">
                  {passages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleJumpToStory(idx)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                        idx === currentStoryIndex
                          ? 'bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white shadow-md'
                          : 'bg-white border-2 border-[#EDE9FF] text-[#6B6FA3] hover:border-[#8E7BFF]'
                      }`}
                      title={`Go to Story ${idx + 1}`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                  
                  {/* Skip to Next Story Button */}
                  {currentStoryIndex < totalStories - 1 && (
                    <Button
                      onClick={handleSkipToNextStory}
                      variant="outline"
                      size="sm"
                      className="ml-2 bg-white hover:bg-[#FFF7EE] border-[#F7B26A] text-[#F7B26A] hover:text-[#E5A05A]"
                      title="Skip to Next Story"
                    >
                      <SkipForward className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Next Story</span>
                    </Button>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs sm:text-sm text-[#6B6FA3]">Question {currentIndex + 1} of {totalQuestions}</div>
                  <div className="flex items-center gap-2 sm:gap-4">
                    <span className="text-xs sm:text-sm font-semibold text-[#8E7BFF]">{currentMotivation}</span>
                    <span className="flex items-center gap-1 text-xs text-[#6B6FA3]">
                      <Clock className="w-3 h-3" />
                      {formatTime(currentQuestionTime)}
                    </span>
                  </div>
                </div>
                <div
                  className="w-full h-3 rounded-full overflow-hidden shadow-sm relative"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(progressPercentage)}
                >
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-[#F3E8FF] via-[#FFF7EE] to-[#F8FBFF] opacity-95 z-0" />
                  <div
                    style={{ width: `${progressPercentage}%` }}
                    className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] shadow-md transition-all z-10"
                  />
                </div>
              </div>

              {/* Toggle Passage Button (Mobile) */}
              {isMobile && (
                <Button
                  onClick={() => setShowPassage(!showPassage)}
                  variant="outline"
                  size="sm"
                  className="mb-4 w-full"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  {showPassage ? 'Hide Story' : 'Show Story'}
                </Button>
              )}

              {/* Content Area */}
              <div className="flex-1 overflow-y-auto">
                {/* Passage Section */}
                {(showPassage || !isMobile) && (
                  <div className="mb-4 bg-gradient-to-br from-[#F3E8FF] to-[#FFF7EE] rounded-2xl p-4 border-2 border-[#EDE9FF]">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-[#8E7BFF]" />
                        <h3 className="font-bold text-[#1F224B]">{currentPassage.title}</h3>
                      </div>
                      
                      {/* Audio Controls */}
                      {currentPassage.audioBase64 && (
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={toggleAudio}
                            variant="outline"
                            size="sm"
                            className="bg-white/80 hover:bg-white p-2"
                            title={isPlaying ? 'Pause' : 'Play Story'}
                          >
                            {isPlaying && currentAudioStoryId === currentPassage.id ? (
                              <Pause className="w-4 h-4 text-[#8E7BFF]" />
                            ) : (
                              <Play className="w-4 h-4 text-[#8E7BFF]" />
                            )}
                          </Button>
                          <Button
                            onClick={restartAudio}
                            variant="outline"
                            size="sm"
                            className="bg-white/80 hover:bg-white p-2"
                            title="Restart Audio"
                          >
                            <RotateCcw className="w-4 h-4 text-[#6B6FA3]" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Audio Progress Bar */}
                    {currentPassage.audioBase64 && currentAudioStoryId === currentPassage.id && audioDuration > 0 && (
                      <div className="mb-3">
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#8E7BFF] to-[#ECA7C1] transition-all"
                            style={{ width: `${(audioProgress / audioDuration) * 100}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-[#6B6FA3] mt-1">
                          <span>{formatTime(audioProgress)}</span>
                          <span>{formatTime(audioDuration)}</span>
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-[#6B6FA3] italic mb-3">{currentPassage.directions}</p>
                    <div className="bg-white/80 rounded-xl p-3">
                      <p className="text-sm leading-relaxed text-[#1F224B] whitespace-pre-line">
                        {currentPassage.content}
                      </p>
                    </div>
                  </div>
                )}

                {/* Question Section */}
                <div className="mb-4">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] flex items-center justify-center text-white font-bold flex-shrink-0">
                      {currentIndex + 1}
                    </div>
                    <p className="text-base sm:text-lg font-semibold text-[#1F224B] leading-relaxed">
                      {currentQuestion.text}
                    </p>
                  </div>

                  {/* Options */}
                  <div className="space-y-3">
                    {currentQuestion.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleSelectOption(index)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          selectedOption === index
                            ? 'bg-gradient-to-r from-[#FFF7EE] to-[#FFF0F6] border-[#F7B26A] shadow-md'
                            : 'bg-white border-[#EDE9FF] hover:border-[#ECA7C1] hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            selectedOption === index ? 'border-[#F7B26A] bg-[#F7B26A]' : 'border-[#6B6FA3]'
                          }`}>
                            {selectedOption === index && (
                              <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                          </div>
                          <span className={`text-sm sm:text-base ${
                            selectedOption === index ? 'text-[#1F224B] font-medium' : 'text-[#6B6FA3]'
                          }`}>
                            {String.fromCharCode(65 + index)}. {option}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-center w-full pt-4 border-t border-[#EDE9FF] flex-wrap">
                <Button
                  onClick={handleNext}
                  disabled={selectedOption === undefined}
                  className={`min-w-[8rem] h-12 rounded-full text-lg font-bold shadow-lg transition-all ${
                    selectedOption === undefined
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'text-white bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-95'
                  }`}
                >
                  {currentIndex === totalQuestions - 1 ? '✓ Finish' : 'Next →'}
                </Button>
                <Button
                  onClick={handleSkip}
                  size="lg"
                  variant="outline"
                  className="min-w-[6.5rem] h-12 rounded-full text-lg font-semibold border-2 border-[#EDE9FF] text-[#6B6FA3] bg-white"
                >
                  Skip
                </Button>
              </div>
            </Card>
          </div>
        </div>

        {/* Right Sidebar: Instructions */}
        <div className="right-col hidden lg:flex flex-col p-6 lg:p-8 bg-gradient-to-b from-blue-50 via-white to-blue-100 rounded-xl shadow-lg min-h-[500px] max-h-full overflow-auto border border-blue-200 lg:w-96 xl:w-[520px] lg:origin-top-right flex-shrink-0 unified-medium-panel" style={{ marginTop: '15px' }}>
          <div className="unified-medium-inner">
            <div className="mt-6 lg:mt-0 sticky top-20 w-full">
              <div className="w-full flex flex-col gap-4 lg:gap-6">
                <h2 className="font-bold text-xl lg:text-2xl mb-4 text-[#55608A]">Story Explorer Instructions</h2>

                {/* Icon */}
                <div className="flex justify-center mb-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden shadow-md border-2 border-gray-200">
                    <img src={login_hero_image} alt="Reading Test Icon" className="w-full h-full object-cover" />
                  </div>
                </div>

                {/* Instructions Card */}
                <div className="bg-gradient-to-br from-[#F3E8FF] to-white p-6 rounded-2xl border-2 border-[#EDE9FF] shadow-sm">
                  <h3 className="font-semibold text-[#1F224B] mb-4">How to Answer:</h3>
                  <ul className="space-y-3 text-sm text-[#6B6FA3]">
                    <li className="flex items-start gap-2">
                      <span className="text-[#F7B26A] font-bold">1.</span>
                      <span>Listen to or read the story carefully</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#F7B26A] font-bold">2.</span>
                      <span>Read the question and all options</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#F7B26A] font-bold">3.</span>
                      <span>Click on the best answer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#F7B26A] font-bold">4.</span>
                      <span>Click "Next" to continue</span>
                    </li>
                  </ul>
                </div>

                {/* Audio Tip */}
                <div className="bg-gradient-to-br from-[#FFF7EE] to-white p-4 rounded-2xl border-2 border-[#F7B26A]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Volume2 className="w-5 h-5 text-[#F7B26A]" />
                    <h3 className="font-semibold text-[#1F224B]">Audio Available</h3>
                  </div>
                  <p className="text-sm text-[#6B6FA3]">
                    Click the play button to listen to the story being read aloud!
                  </p>
                </div>

                {/* Tips */}
                <div className="space-y-3 text-base lg:text-lg mt-4 w-full">
                  <p className="text-[#6B6FA3] flex items-center gap-2">
                    <span className="text-xl">💡</span>
                    <span>You can skip and return to questions later</span>
                  </p>
                  <p className="text-[#6B6FA3] flex items-center gap-2">
                    <span className="text-xl">📖</span>
                    <span>Re-read the story if needed</span>
                  </p>
                  <p className="text-[#6B6FA3] flex items-center gap-2">
                    <span className="text-xl">🔊</span>
                    <span>Listen to the story as many times as you need</span>
                  </p>
                </div>

                {/* End Test Button */}
                <button
                  onClick={handleSubmit}
                  className="w-full mb-2 py-3 lg:py-5 rounded-lg text-white text-base lg:text-xl lg:font-extrabold shadow transition"
                  style={{ background: 'linear-gradient(90deg,#F7B26A,#ECA7C1)' }}
                >
                  End Test
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer style={{ marginTop: 10, color: '#6B6FA3', fontSize: 12, textAlign: 'center' }}>© The Dear Parent Project</footer>
    </div>
  );
};

export default ReadingTest;